/**
 * scheduleDates — campaign start/end date transforms for the admin console.
 *
 * EVERY FUNCTION HERE IS A STRING TRANSFORM. No stored value is ever fed through
 * `new Date()` and back out again, and that is the entire defence against the
 * off-by-one this repo has already been bitten by twice (see the header of
 * fmtDate.js). Two concrete traps it avoids:
 *
 *   new Date('2026-10-31 00:00:00').toISOString().slice(0,10)
 *     V8 parses the space form as LOCAL time, then toISOString shifts to UTC, so
 *     any viewer east of UTC gets '2026-10-30'.
 *
 *   new Date('2026-10-31').toLocaleDateString()
 *     The bare date form is parsed as UTC MIDNIGHT, so any viewer west of UTC
 *     gets Oct 30. This was live in the launch-date panel this replaces, which
 *     rendered a stored launch date via new Date(v).toLocaleDateString("en-US").
 *
 * Being string-only also makes these functions timezone-independent by
 * construction rather than by test setup, which is why their spec needs no
 * process.env.TZ juggling.
 *
 * TWO STORED FORMATS. The API column is VARCHAR and holds both
 * 'YYYY-MM-DD HH:mm:ss' (the cron and, as of 2026-07-31, every writer) and
 * '…T…Z' (legacy rows). Everything here accepts both.
 */

/** Leading calendar date of any stored shape, or ''. */
export function toDateInputValue(stored) {
  if (!stored) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(stored))
  return m ? `${m[1]}-${m[2]}-${m[3]}` : ''
}

/** 'YYYY-MM-DDTHH:mm' for <input type="datetime-local">, or ''. */
export function toDateTimeLocalValue(stored) {
  if (!stored) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(stored))
  if (m) return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`
  const d = toDateInputValue(stored)
  return d ? `${d}T00:00` : ''
}

/**
 * What the API is sent. The server stores this verbatim as a wall clock in the
 * platform's business timezone (America/Toronto) — see campaignSchedule.js — so
 * the console must NOT convert. A datetime-local value the browser reinterprets
 * is exactly how a timezone gets invented.
 */
export function toApiDateTime(inputValue) {
  if (!inputValue) return null
  const s = String(inputValue).trim()
  const dt = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(s)
  if (dt) return `${dt[1]} ${dt[2]}:${dt[3]}:${dt[4] || '00'}`
  const d = /^(\d{4}-\d{2}-\d{2})$/.exec(s)
  if (d) return `${d[1]} 00:00:00`
  return null
}

/**
 * launch + 3 months, matching the API's defaultCampaignEndDate. Month arithmetic
 * on the calendar date only — no Date object, so no drift. Clamps a rolled-over
 * day (31 Jan + 1 month) to the last day of the target month, which is what
 * moment's add(1,'M') does on the server.
 */
export function defaultEndFromLaunch(launchDate, months = 3) {
  const base = toDateInputValue(launchDate)
  if (!base) return ''
  const [y, m, d] = base.split('-').map(Number)
  const total = (m - 1) + months
  const year = y + Math.floor(total / 12)
  const month = (total % 12) + 1
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const day = Math.min(d, lastDay)
  const pad = n => String(n).padStart(2, '0')
  const time = /[ T](\d{2}:\d{2}:\d{2})/.exec(String(launchDate))
  return `${year}-${pad(month)}-${pad(day)} ${time ? time[1] : '00:00:00'}`
}

/** True when `next` closes the campaign EARLIER than `current`. */
export function isShortening(currentEnd, nextEnd) {
  const a = toDateInputValue(currentEnd)
  const b = toDateInputValue(nextEnd)
  if (!a || !b) return false
  return b < a // both 'YYYY-MM-DD', so lexicographic IS chronological
}

/** Whole days between two stored dates; negative when `next` is earlier. */
export function daysBetween(currentEnd, nextEnd) {
  const a = toDateInputValue(currentEnd)
  const b = toDateInputValue(nextEnd)
  if (!a || !b) return 0
  const [ay, am, ad] = a.split('-').map(Number)
  const [by, bm, bd] = b.split('-').map(Number)
  const ms = Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)
  return Math.round(ms / 86400000)
}

/** Today, as a value for an <input type="date"> `min` attribute. */
export function todayDateInputValue(now = new Date()) {
  const pad = n => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/**
 * Server refusal codes → operator-facing copy. Keyed on `code`, never on the
 * server's message text: the message is prose that will be reworded, the code is
 * the contract.
 */
export const SCHEDULE_ERROR_COPY = {
  campaign_id_required: 'That campaign could not be identified. Reload the page and try again.',
  campaign_not_found: 'This campaign is no longer in the system.',
  no_schedule_fields: 'Set a start date, an end date, or both before saving.',
  invalid_launch_date: 'That start date is not a valid date before 19 January 2038.',
  invalid_end_date: 'That end date could not be read. Use the date picker.',
  end_date_not_after_start: 'The end date must fall after the start date.',
  end_date_in_past:
    'That end date has already passed, so the campaign would close on the next sweep. '
    + 'Use Close Campaign if that is what you intend.',
  shorten_requires_confirmation:
    'Shortening a live campaign needs confirmation. Try again and confirm the warning.',
  launch_date_immutable_after_publish:
    'This campaign is already live, so its start date is a matter of record. '
    + 'Use Withdraw and Reschedule to take it off the market first.',
  schedule_not_allowed_for_status:
    'This campaign is not in a state where its schedule can be changed.',
  fund_schedule_unsupported:
    'Fund campaigns run continuously and are not closed by an end date.',
}
